import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDate } from "@/lib/dates";
import { fmtMoney, fmtDecimalHours, fmtHM } from "@/lib/time";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const REPORT_TYPES = ["time", "matter-activity", "expenses", "unbilled", "invoices", "revenue"];

const p2 = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;

function quickRange(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case "this_month":
      return [toIso(new Date(y, m, 1)), toIso(now)];
    case "prev_month":
      return [toIso(new Date(y, m - 1, 1)), toIso(new Date(y, m, 0))];
    case "this_quarter":
      return [toIso(new Date(y, Math.floor(m / 3) * 3, 1)), toIso(now)];
    case "this_year":
      return [`${y}-01-01`, toIso(now)];
    case "prev_year":
      return [`${y - 1}-01-01`, `${y - 1}-12-31`];
    default:
      return ["", ""];
  }
}

const RANGE_KEYS = ["all", "this_month", "prev_month", "this_quarter", "this_year", "prev_year", "custom"];

export default function Reports() {
  const { t, lang } = useT();
  const [rtype, setRtype] = useState("time");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [matters, setMatters] = useState([]);
  const [range, setRange] = useState("all");
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", client: "", matter_id: "", matter_status: "",
    responsible_lawyer: "", billing_status: "", invoice_status: "",
  });
  const [groupBy, setGroupBy] = useState("none");
  const [includes, setIncludes] = useState({
    include_time: true, include_tasks: true, include_deadlines: true,
    include_expenses: true, include_invoices: true,
  });

  useEffect(() => {
    api.get("/matters").then((r) => setMatters(r.data)).catch(() => {});
  }, []);

  // Translate stored canonical values only when a translation exists.
  const tv = (v) => {
    if (!v) return "—";
    const tr = t(`val.${v}`);
    return tr === `val.${v}` ? v : tr;
  };

  const COLS = useMemo(() => ({
    time: [
      { labelKey: "reports.col.date", get: (r) => formatDate(r.date) },
      { labelKey: "reports.col.client", get: (r) => r.client },
      { labelKey: "reports.col.matter", get: (r) => r.matter },
      { labelKey: "reports.col.description", get: (r) => (r.billing_type && r.billing_type !== "Hourly" ? `${r.description} · ${tv(r.billing_type)}` : r.description) },
      { labelKey: "reports.col.actual", align: "right", get: (r) => fmtHM(r.actual_minutes) },
      { labelKey: "reports.col.billable", align: "right", get: (r) => (!r.billing_type || r.billing_type === "Hourly" ? fmtDecimalHours(r.billable_minutes) : "—") },
      { labelKey: "reports.col.rate", align: "right", get: (r) => (!r.billing_type || r.billing_type === "Hourly" ? fmtMoney(r.hourly_rate) : "—") },
      { labelKey: "reports.col.amount", align: "right", get: (r) => fmtMoney(r.amount) },
      { labelKey: "reports.col.billing_status", get: (r) => tv(r.billing_status) },
    ],
    expenses: [
      { labelKey: "reports.col.date", get: (r) => formatDate(r.date) },
      { labelKey: "reports.col.client", get: (r) => r.client },
      { labelKey: "reports.col.matter", get: (r) => r.matter },
      { labelKey: "reports.col.description", get: (r) => r.description },
      { labelKey: "reports.col.category", get: (r) => r.category_other || tv(r.category) },
      { labelKey: "reports.col.amount", align: "right", get: (r) => fmtMoney(r.amount) },
      { labelKey: "reports.col.vat", align: "right", get: (r) => (r.vat != null ? fmtMoney(r.vat) : "—") },
      { labelKey: "reports.col.billable_client", get: (r) => t(r.billable_to_client ? "val.Yes" : "val.No") },
      { labelKey: "reports.col.billing_status", get: (r) => tv(r.billing_status) },
      { labelKey: "reports.col.invoice_no", get: (r) => r.invoice_number || "—" },
    ],
    unbilled: [
      { labelKey: "reports.col.type", get: (r) => t(`reports.kind.${r.kind}`) },
      { labelKey: "reports.col.date", get: (r) => formatDate(r.date) },
      { labelKey: "reports.col.client", get: (r) => r.client },
      { labelKey: "reports.col.matter", get: (r) => r.matter },
      { labelKey: "reports.col.description", get: (r) => r.description },
      { labelKey: "reports.col.hours", align: "right", get: (r) => (r.kind === "time" ? fmtDecimalHours(r.billable_minutes) : "—") },
      { labelKey: "reports.col.value", align: "right", get: (r) => fmtMoney(r.amount) },
      {
        labelKey: "reports.col.allocation",
        get: (r) => (r.allocated_invoice ? t("reports.allocated_draft", { n: r.allocated_invoice }) : "—"),
      },
    ],
    invoices: [
      { labelKey: "reports.col.number", get: (r) => r.number },
      { labelKey: "reports.col.invoice_date", get: (r) => formatDate(r.invoice_date) },
      { labelKey: "reports.col.due", get: (r) => formatDate(r.due_date) },
      { labelKey: "reports.col.client", get: (r) => r.client },
      { labelKey: "reports.col.matter", get: (r) => r.matter },
      { labelKey: "reports.col.fees", align: "right", get: (r) => fmtMoney(r.fees) },
      { labelKey: "reports.col.vat", align: "right", get: (r) => fmtMoney(r.vat) },
      { labelKey: "reports.col.expenses", align: "right", get: (r) => fmtMoney(r.expenses) },
      { labelKey: "reports.col.total", align: "right", get: (r) => fmtMoney(r.total) },
      { labelKey: "reports.col.status", get: (r) => tv(r.status) },
    ],
    revenue: [
      { labelKey: "reports.col.number", get: (r) => r.number },
      { labelKey: "reports.col.invoice_date", get: (r) => formatDate(r.invoice_date) },
      { labelKey: "reports.col.due", get: (r) => formatDate(r.due_date) },
      { labelKey: "reports.col.client", get: (r) => r.client },
      { labelKey: "reports.col.matter", get: (r) => r.matter },
      { labelKey: "reports.col.fees", align: "right", get: (r) => fmtMoney(r.fees) },
      { labelKey: "reports.col.vat", align: "right", get: (r) => fmtMoney(r.vat) },
      { labelKey: "reports.col.expenses", align: "right", get: (r) => fmtMoney(r.expenses) },
      { labelKey: "reports.col.total", align: "right", get: (r) => fmtMoney(r.total) },
      { labelKey: "reports.col.status", get: (r) => tv(r.status) },
    ],
    "matter-activity": [
      { labelKey: "reports.col.date", get: (r) => formatDate(r.date) },
      { labelKey: "reports.col.type", get: (r) => t(`reports.cat.${r.category}`) },
      { labelKey: "reports.col.description", get: (r) => (r.category === "time" && r.billing_type && r.billing_type !== "Hourly" ? `${r.title} · ${tv(r.billing_type)}` : r.title) },
      { labelKey: "reports.col.status", get: (r) => [tv(r.detail), tv(r.status)].filter((x) => x && x !== "—").join(" · ") },
      { labelKey: "reports.col.amount", align: "right", get: (r) => (r.amount != null ? fmtMoney(r.amount) : "—") },
    ],
  }), [lang]);

  const TOTALS = useMemo(() => ({
    time: (tot) => [
      ["reports.total_actual", fmtHM(tot.actual_minutes)],
      ["reports.total_billable", fmtDecimalHours(tot.billable_minutes)],
      ["reports.total_value", fmtMoney(tot.value)],
      ["reports.total_unbilled", fmtMoney(tot.unbilled_value)],
    ],
    expenses: (tot) => [
      ["reports.total_amount", fmtMoney(tot.amount)],
      ["reports.total_vat", fmtMoney(tot.vat)],
      ["reports.grand_total", fmtMoney(tot.total)],
    ],
    unbilled: (tot) => [
      ["reports.unbilled_hours", fmtDecimalHours(tot.billable_minutes)],
      ["reports.unbilled_time_value", fmtMoney(tot.time_value)],
      ["reports.unbilled_expenses", fmtMoney(tot.expenses_value)],
      ["reports.total_potential", fmtMoney(tot.total)],
    ],
    invoices: (tot) => [
      ["reports.invoice_count", String(tot.count)],
      ["reports.col.fees", fmtMoney(tot.fees)],
      ["reports.col.vat", fmtMoney(tot.vat)],
      ["reports.col.expenses", fmtMoney(tot.expenses)],
      ["reports.grand_total", fmtMoney(tot.total)],
    ],
    revenue: (tot) => [
      [`reports.issued_total`, `${fmtMoney(tot.issued_total)} (${tot.issued_count})`],
      [`reports.paid_total`, `${fmtMoney(tot.paid_total)} (${tot.paid_count})`],
    ],
    "matter-activity": (tot) => [
      ["reports.total_billable", fmtDecimalHours(tot.time_minutes)],
      ["reports.time_value", fmtMoney(tot.time_value)],
      ["reports.expenses_total", fmtMoney(tot.expenses_total)],
      ["reports.invoiced_total", fmtMoney(tot.invoiced_total)],
    ],
  }), [lang]);

  const lawyers = useMemo(
    () => [...new Set(matters.map((m) => m.responsible_lawyer).filter(Boolean))],
    [matters]
  );

  const params = useMemo(() => {
    const p = {};
    for (const [k, v] of Object.entries(filters)) if (v) p[k] = v;
    if (rtype === "matter-activity") {
      for (const [k, v] of Object.entries(includes)) p[k] = v ? "true" : "false";
    }
    return p;
  }, [filters, includes, rtype]);

  useEffect(() => {
    if (rtype === "matter-activity" && !filters.matter_id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError("");
    api
      .get(`/reports/${rtype}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(formatApiError(e));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [rtype, params]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v.target ? v.target.value : v }));

  const onRange = (key) => {
    setRange(key);
    if (key === "all") setFilters((f) => ({ ...f, date_from: "", date_to: "" }));
    else if (key !== "custom") {
      const [from, to] = quickRange(key);
      setFilters((f) => ({ ...f, date_from: from, date_to: to }));
    }
  };

  const cols = COLS[rtype];
  const rows = data?.rows || [];

  const groupedRows = useMemo(() => {
    if (rtype !== "unbilled" || groupBy === "none") return null;
    const key = groupBy === "client" ? "client" : "matter";
    const groups = {};
    for (const r of rows) {
      (groups[r[key] || "—"] = groups[r[key] || "—"] || []).push(r);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([name, rs]) => ({
      name,
      rows: rs,
      subtotal: rs.reduce((s, r) => s + r.amount, 0),
    }));
  }, [rows, rtype, groupBy]);

  const pdfUrl = () => {
    const qs = new URLSearchParams({ ...params, lang }).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/reports/${rtype}/pdf?${qs}`;
  };

  const exportCsv = () => {
    const escCsv = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map((c) => escCsv(t(c.labelKey))).join(";")];
    for (const r of rows) lines.push(cols.map((c) => escCsv(c.get(r))).join(";"));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rplaw-${rtype}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(t("reports.csv"));
  };

  const selCls = "h-9 text-xs";
  const showDates = true;
  const showClient = rtype !== "matter-activity";
  const showMatter = true;
  const showMatterStatus = ["time", "expenses", "unbilled"].includes(rtype);
  const showLawyer = ["time", "expenses", "unbilled"].includes(rtype);
  const showBillingStatus = ["time", "expenses"].includes(rtype);
  const showInvoiceStatus = ["invoices", "revenue"].includes(rtype);

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("reports.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("reports.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-print-hidden="true">
          <Button variant="outline" data-testid="report-print-button" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-1.5" /> {t("reports.print")}
          </Button>
          <a href={pdfUrl()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="report-pdf-button">
              <FileDown className="h-4 w-4 mr-1.5" /> {t("reports.pdf")}
            </Button>
          </a>
          <Button variant="outline" data-testid="report-csv-button" onClick={exportCsv} disabled={!rows.length}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {t("reports.csv")}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="report-type-tabs" data-print-hidden="true">
        {REPORT_TYPES.map((rt) => (
          <button
            key={rt}
            data-testid={`report-type-${rt}`}
            onClick={() => setRtype(rt)}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              rtype === rt
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {t(`reports.type.${rt}`)}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="report-filters" data-print-hidden="true">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t("reports.filters")}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">{t("reports.quick_range")}</Label>
            <Select value={range} onValueChange={onRange}>
              <SelectTrigger data-testid="report-quick-range" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`report-range-${k}`}>{t(`reports.range.${k}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showDates && (
            <>
              <div>
                <Label className="text-xs">{t("reports.date_from")}</Label>
                <div className="mt-1">
                  <DateInput testid="report-date-from" value={filters.date_from}
                    onChange={(v) => { setRange("custom"); setF("date_from")(v ?? ""); }} />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("reports.date_to")}</Label>
                <div className="mt-1">
                  <DateInput testid="report-date-to" value={filters.date_to}
                    onChange={(v) => { setRange("custom"); setF("date_to")(v ?? ""); }} />
                </div>
              </div>
            </>
          )}
          {showClient && (
            <div>
              <Label className="text-xs">{t("reports.client")}</Label>
              <Input data-testid="report-client-filter" value={filters.client} onChange={setF("client")}
                placeholder={t("reports.client_ph")} className={`mt-1 ${selCls}`} />
            </div>
          )}
          {showMatter && (
            <div>
              <Label className="text-xs">{t("reports.matter")}</Label>
              <Select value={filters.matter_id || "all"} onValueChange={(v) => setF("matter_id")(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-matter-filter" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rtype !== "matter-activity" && (
                    <SelectItem value="all" data-testid="report-matter-all">{t("reports.all")}</SelectItem>
                  )}
                  {matters.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`report-matter-${m.internal_ref || m.id}`}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showMatterStatus && (
            <div>
              <Label className="text-xs">{t("reports.matter_status")}</Label>
              <Select value={filters.matter_status || "all"} onValueChange={(v) => setF("matter_status")(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-matter-status" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reports.all")}</SelectItem>
                  {["Active", "Pending", "Closed"].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`report-matter-status-${s.toLowerCase()}`}>{t(`val.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showLawyer && (
            <div>
              <Label className="text-xs">{t("reports.lawyer")}</Label>
              <Select value={filters.responsible_lawyer || "all"} onValueChange={(v) => setF("responsible_lawyer")(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-lawyer-filter" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reports.all")}</SelectItem>
                  {lawyers.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showBillingStatus && (
            <div>
              <Label className="text-xs">{t("reports.billing_status")}</Label>
              <Select value={filters.billing_status || "all"} onValueChange={(v) => setF("billing_status")(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-billing-status" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reports.all")}</SelectItem>
                  {["Unbilled", "Billed"].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`report-billing-${s.toLowerCase()}`}>{t(`val.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showInvoiceStatus && (
            <div>
              <Label className="text-xs">{t("reports.invoice_status")}</Label>
              <Select value={filters.invoice_status || "all"} onValueChange={(v) => setF("invoice_status")(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-invoice-status" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reports.all")}</SelectItem>
                  {(rtype === "revenue" ? ["Issued", "Paid"] : ["Draft", "Issued", "Paid", "Cancelled"]).map((s) => (
                    <SelectItem key={s} value={s} data-testid={`report-invoice-status-${s.toLowerCase()}`}>{t(`val.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {rtype === "unbilled" && (
            <div>
              <Label className="text-xs">{t("reports.group_by")}</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger data-testid="report-group-by" className={`mt-1 ${selCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="report-group-none">{t("reports.group_none")}</SelectItem>
                  <SelectItem value="client" data-testid="report-group-client">{t("reports.group_client")}</SelectItem>
                  <SelectItem value="matter" data-testid="report-group-matter">{t("reports.group_matter")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {rtype === "matter-activity" && (
          <div className="mt-3 flex flex-wrap items-center gap-4" data-testid="report-include-toggles">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("reports.include")}:</span>
            {[
              ["include_time", "reports.inc_time"],
              ["include_tasks", "reports.inc_tasks"],
              ["include_deadlines", "reports.inc_deadlines"],
              ["include_expenses", "reports.inc_expenses"],
              ["include_invoices", "reports.inc_invoices"],
            ].map(([k, key]) => (
              <label key={k} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`report-toggle-${k}`}>
                <input
                  type="checkbox"
                  className="accent-slate-800"
                  checked={includes[k]}
                  onChange={(e) => setIncludes((cur) => ({ ...cur, [k]: e.target.checked }))}
                />
                {t(key)}
              </label>
            ))}
          </div>
        )}
      </div>

      <div data-testid="report-print-area">
        <div className="mb-3">
          <h2 className="font-serif text-2xl text-slate-900" data-testid="report-title">{t(`reports.type.${rtype}`)}</h2>
          <div className="text-xs text-slate-500 mt-1 space-x-3">
            {(filters.date_from || filters.date_to) && (
              <span>{t("reports.period")}: {formatDate(filters.date_from) || "…"} – {formatDate(filters.date_to) || "…"}</span>
            )}
            {filters.client && <span>{t("reports.client")}: {filters.client}</span>}
            {filters.matter_id && (
              <span>{t("reports.matter")}: {matters.find((m) => m.id === filters.matter_id)?.name}</span>
            )}
            <span>{t("reports.generated")}: {formatDate(new Date().toISOString().slice(0, 10))}</span>
          </div>
        </div>

        {rtype === "matter-activity" && !filters.matter_id ? (
          <div className="text-sm text-slate-500" data-testid="report-select-matter">{t("reports.select_matter")}</div>
        ) : loading ? (
          <div className="text-sm text-slate-500" data-testid="report-loading">{t("reports.loading")}</div>
        ) : error ? (
          <div className="text-sm text-red-700" data-testid="report-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="report-empty">{t("reports.empty")}</div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="report-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left uppercase tracking-wider text-slate-500">
                    {cols.map((c) => (
                      <th key={c.labelKey} className={`px-3 py-2.5 ${c.align === "right" ? "text-right" : ""}`}>
                        {t(c.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedRows
                    ? groupedRows.map((g) => (
                        <>
                          <tr key={g.name} className="bg-slate-100/70 border-t border-slate-200" data-testid={`report-group-${g.name}`}>
                            <td colSpan={cols.length - 1} className="px-3 py-2 font-semibold text-slate-800">{g.name}</td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtMoney(g.subtotal)}</td>
                          </tr>
                          {g.rows.map((r, i) => (
                            <tr key={`${g.name}-${i}`} className="border-t border-slate-100">
                              {cols.map((c) => (
                                <td key={c.labelKey} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""} text-slate-700`}>
                                  {c.get(r)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </>
                      ))
                    : rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`report-row-${i}`}>
                          {cols.map((c) => (
                            <td key={c.labelKey} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""} text-slate-700`}>
                              {c.get(r)}
                            </td>
                          ))}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5" data-testid="report-totals">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t("reports.totals")}</div>
              <div className="flex flex-wrap gap-x-10 gap-y-2 text-sm">
                {TOTALS[rtype](data.totals).map(([labelKey, value]) => (
                  <div key={labelKey} className="flex items-baseline gap-2">
                    <span className="text-slate-500">{t(labelKey)}:</span>
                    <span className="font-bold text-slate-900" data-testid={`report-total-${labelKey.split(".").pop()}`}>{value}</span>
                  </div>
                ))}
              </div>
              {rtype === "revenue" && (
                <p className="text-[11px] text-slate-400 mt-3">{t("reports.revenue_note")}</p>
              )}
            </div>

            {rtype === "revenue" && data.payments && (
              <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5" data-testid="report-payments">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("reports.payments_received")}</div>
                  <div className="text-sm" data-testid="report-payments-total">
                    <span className="font-bold text-slate-900">{fmtMoney(data.payments.total_received)}</span>
                    <span className="text-slate-500"> ({data.payments.count})</span>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mb-3" data-testid="report-payments-split">
                  {fmtMoney(data.payments.total_received_rplaw ?? 0)} {t("reports.payments_split_rplaw")} · {fmtMoney(data.payments.total_received_imported ?? 0)} {t("reports.payments_split_imported")}
                </div>
                {data.payments.rows.length > 0 && (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="py-1.5">{t("reports.col.date")}</th>
                        <th className="py-1.5">{t("reports.col.invoice_no")}</th>
                        <th className="py-1.5">{t("reports.col.client")}</th>
                        <th className="py-1.5 text-right">{t("reports.col.amount")}</th>
                        <th className="py-1.5">{t("reports.col_method")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.rows.map((p, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`report-payment-row-${i}`}>
                          <td className="py-1.5 text-slate-600">{formatDate(p.date)}</td>
                          <td className="py-1.5 text-slate-700">{p.invoice_no}</td>
                          <td className="py-1.5 text-slate-700">{p.client}</td>
                          <td className="py-1.5 text-right text-slate-800">{fmtMoney(p.amount)}</td>
                          <td className="py-1.5 text-slate-600">{p.method || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-3">{t("reports.payments_note")}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
